import React, { Dispatch, SetStateAction } from 'react';
import { useForm, SubmitHandler, FormProvider } from 'react-hook-form';
import { Button, Stack } from '@mui/material';
import {
  StyledStatusBox,
  StyledTypographySpan,
  StyledTypographySubTitle,
  StyledTypographyTitle
} from '../../styled-components';
import CustomInputForm from '../../../components/InputsForm/CustomInput';
import { UserInfoIcon } from 'assets/icons/UserInfo.icon';
import { PhoneIcon } from 'assets/icons/Phone.icon';
import { EmailIcon } from 'assets/icons/Email.icon';
import { CalenderIcon } from 'assets/icons/Calender.icon';
import { PulseIcon } from 'assets/icons/Pulse.icon';
import {
  IUser,
  USERS_FIELDS,
  UsersAndPermissionFormLabels,
  UsersErrorMessages,
  UsersKey
} from 'constants/user.constants';
import { dirtyValues, formResolver, getFormattedDate, isPhoneNumberValid } from '../../../utils/global.util';
import { emailValidation } from 'components/AuthorizationForm/utils/passwordValidation';

interface IFormInputs {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
}

interface IProfileProps extends Partial<IUser> {
  disabled: boolean;
  setOnEdit: Dispatch<SetStateAction<boolean>>;
  onUpdateUser: (data: Partial<IFormInputs>) => void;
}

const UserProfileForm: React.FC<IProfileProps> = (props: IProfileProps) => {
  const {
    firstName,
    lastName,
    active,
    isSuperAdmin,
    phoneNumber,
    email,
    disabled,
    lastLogin,
    setOnEdit,
    onUpdateUser,
    createdAt,
    updatedAt,
    createdUserFirstName,
    createdUserLastName,
    updatedUserFirstName,
    updatedUserLastName
  } = props;

  const defaultValues = {
    firstName: firstName || '',
    lastName: lastName || '',
    phoneNumber: phoneNumber || '',
    email: email || ''
  };

  const methods = useForm<IFormInputs>({
    defaultValues: defaultValues,
    mode: 'onTouched',
    resolver: (values) => {
      const result = formResolver({ dataForm: values }, USERS_FIELDS);

      const emailError =
        values.email && !emailValidation(values.email)
          ? { email: { type: 'pattern', message: UsersErrorMessages.INVALID_EMAIL } }
          : {};

      const phoneError =
        values.phoneNumber && !isPhoneNumberValid(values.phoneNumber)
          ? { phoneNumber: { type: 'pattern', message: UsersErrorMessages.INVALID_PHONE_NUMBER } }
          : {};

      result.errors.dataForm = {
        ...result.errors.dataForm,
        ...emailError,
        ...phoneError
      };

      return {
        values: result.values.dataForm,
        errors: result.errors.dataForm || {}
      };
    }
  });

  const onCancel = () => {
    methods.reset(defaultValues);
    setOnEdit(false);
  };

  const {
    formState: { errors }
  } = methods;

  const currentValues = methods.watch();

  const isSaveDisabled =
    Object.keys(errors).length > 0 ||
    (currentValues.firstName === (firstName || '') &&
      currentValues.lastName === (lastName || '') &&
      currentValues.phoneNumber === (phoneNumber || '') &&
      currentValues.email === (email || ''));

  const formSubmitHandler: SubmitHandler<IFormInputs> = (data: IFormInputs) => {
    const parsedData = dirtyValues(methods.formState.dirtyFields, data) as Partial<IFormInputs>;
    onUpdateUser(parsedData);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(formSubmitHandler)}>
        <Stack spacing={3}>
          <StyledTypographyTitle>My Profile</StyledTypographyTitle>
          {!disabled && (
            <Button
              color='secondary'
              data-testid='edit-button'
              variant='outlined'
              onClick={() => {
                setOnEdit(true);
              }}
            >
              Edit
            </Button>
          )}
          <Stack spacing={1} direction='row' display='flex' alignItems='center'>
            <StyledTypographySubTitle>{`${firstName} ${lastName}`}</StyledTypographySubTitle>
            <StyledStatusBox isInactive={!active}>{active ? 'Active' : 'Inactive'}</StyledStatusBox>
          </Stack>
          <StyledTypographySpan>{isSuperAdmin ? 'Super admin' : ''}</StyledTypographySpan>
          <Stack spacing={2}>
            {disabled && (
              <Stack direction='row' spacing={2}>
                <CustomInputForm
                  disabled={!disabled}
                  defaultValue={firstName}
                  controllerName={UsersKey.FIRST_NAME}
                  inputRegister={UsersKey.FIRST_NAME}
                  inputId={UsersKey.FIRST_NAME}
                  label={UsersAndPermissionFormLabels.FirstName}
                  type={'text'}
                  icon={<UserInfoIcon />}
                  errorMessage={errors?.firstName?.message}
                />
                <CustomInputForm
                  disabled={!disabled}
                  defaultValue={lastName}
                  controllerName={UsersKey.LAST_NAME}
                  inputRegister={UsersKey.LAST_NAME}
                  inputId={UsersKey.LAST_NAME}
                  label={UsersAndPermissionFormLabels.LastName}
                  type={'text'}
                  icon={<UserInfoIcon />}
                  errorMessage={errors?.lastName?.message}
                />
              </Stack>
            )}
            <Stack direction='row' spacing={2}>
              <CustomInputForm
                disabled={!disabled}
                defaultValue={phoneNumber}
                controllerName={UsersKey.PHONE_NUMBER}
                inputRegister={UsersKey.PHONE_NUMBER}
                inputId={UsersKey.PHONE_NUMBER}
                label={UsersAndPermissionFormLabels.PhoneNumber}
                type={'text'}
                icon={<PhoneIcon />}
                errorMessage={errors?.phoneNumber?.message}
              />
              <CustomInputForm
                disabled={true}
                defaultValue={email}
                controllerName={UsersKey.EMAIL}
                inputRegister={UsersKey.EMAIL}
                inputId={UsersKey.EMAIL}
                label={UsersAndPermissionFormLabels.Email}
                type={UsersKey.EMAIL}
                icon={<EmailIcon />}
                errorMessage={errors?.email?.message}
              />
            </Stack>
            <Stack direction='column' spacing={2}>
              <CustomInputForm
                disabled={true}
                defaultValue={`${createdUserFirstName} ${createdUserLastName} ${getFormattedDate(
                  new Date(createdAt || '')
                )}`}
                controllerName={'createdBy'}
                inputRegister={'createdBy'}
                inputId={'createdBy'}
                label={'Created By'}
                type={'text'}
                icon={<CalenderIcon />}
              />
              <CustomInputForm
                disabled={true}
                defaultValue={getFormattedDate(new Date(lastLogin || ''))}
                controllerName={'lastLogin'}
                inputRegister={'lastLogin'}
                inputId={'lastLogin'}
                label={'Last Login'}
                type={'text'}
                icon={<PulseIcon />}
              />
              <CustomInputForm
                disabled={true}
                defaultValue={`${updatedUserFirstName} ${updatedUserLastName} ${getFormattedDate(
                  new Date(updatedAt || '')
                )}`}
                controllerName={'lastEdit'}
                inputRegister={'lastEdit'}
                inputId={'lastEdit'}
                label={'Last Edit'}
                type={'text'}
                icon={<UserInfoIcon />}
              />
            </Stack>
          </Stack>
          {disabled && (
            <Stack spacing={2} direction='row' sx={{ display: 'flex', justifyContent: 'space-between', m: 10 }}>
              <Button color='secondary' variant='text' data-testid='cancel-button' size='large' onClick={onCancel}>
                cancel
              </Button>
              <Button
                disabled={isSaveDisabled}
                variant='contained'
                color='secondary'
                data-testid='save-button'
                type='submit'
                size='large'
              >
                save
              </Button>
            </Stack>
          )}
        </Stack>
      </form>
    </FormProvider>
  );
};

export default UserProfileForm;
